/**
 * Shared form fields for OrderSource — used by CreateOrderSourceForm and
 * CreateSourceForm.
 */

import { useStore } from '@tanstack/react-form';
import { Field, FieldContent, FieldError, FieldLabel, FieldLabelRow } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';
import { useFormContext } from '../../Form/FormProvider';
import { shouldShowFieldError, useFieldErrorVisibility } from '../../Form/fieldErrorVisibility';
import { useTranslation } from '@hooks/useTranslation';
import { FEE_TYPES } from '../../../schemas/orderSource.schema';
import { FeeTypes, type FeeType } from '@api/orderSources';
import { cn } from '@/lib/utils';

import { FIELD_SURFACE, FIELD_SURFACE_BORDERED } from "@/components/ui/field-surface";
const LABEL_CLASS = 'font-inter text-sm font-semibold text-grey leading-normal';
const INPUT_CLASS =
  cn(FIELD_SURFACE_BORDERED, 'h-[45px] w-full px-6 py-4 font-inter text-sm text-off-black placeholder:text-grey-light focus-visible:outline-none focus-visible:ring-0');
const TEXTAREA_CLASS =
  cn(FIELD_SURFACE, 'h-[84px] resize-none px-6 py-4 font-inter text-sm text-off-black placeholder:text-grey-light focus-visible:ring-0');
// The base Input/SelectTrigger `aria-invalid:` Tailwind variants don't compile
// in this build, so invalid state is an explicit class like the rest of the
// create/edit forms (CreateCustomerForm, CreateOrderForm, etc).
const INVALID_CLASS = 'border-chilli-red-light bg-chilli-blush';

export function OrderSourceFormFields() {
  const { form } = useFormContext();
  const { t } = useTranslation();
  const submitted = useFieldErrorVisibility();
  // Subscribe to fee_type so the Fee input's $/% affordance updates live as the
  // dropdown changes. useStore only re-renders this component when fee_type
  // actually changes (not on every keystroke in other fields).
  const feeType = useStore(
    form.store,
    (s) => (s.values as { fee_type?: FeeType | null }).fee_type ?? null,
  );

  return (
    <div className="flex flex-col gap-8">
      {/* Source name */}
      <form.Field name="name">
        {(field) => {
          const isInvalid = shouldShowFieldError(field.state.meta, submitted);
          return (
            <Field>
              <FieldLabel className={LABEL_CLASS}>{t('form.source', 'Source')}</FieldLabel>
              <FieldContent>
                <Input
                  type="text"
                  aria-invalid={isInvalid || undefined}
                  value={field.state.value as string}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder={t('form.placeholders.source', 'Art House Limited')}
                  className={cn(INPUT_CLASS, isInvalid && INVALID_CLASS)}
                />
                {isInvalid && <FieldError errors={field.state.meta.errors} />}
              </FieldContent>
            </Field>
          );
        }}
      </form.Field>

      {/* Fee type + Fee */}
      <div className="grid grid-cols-2 gap-8">
        <form.Field name="fee_type">
          {(field) => {
            const isInvalid = shouldShowFieldError(field.state.meta, submitted);
            return (
              <Field>
                <FieldLabel className={LABEL_CLASS}>{t('form.fee_type', 'Fee type')}</FieldLabel>
                <FieldContent>
                  <Select
                    value={(field.state.value as string) ?? ''}
                    onValueChange={(value) =>
                      field.handleChange(value === '' ? null : value)
                    }
                  >
                    <SelectTrigger
                      aria-invalid={isInvalid || undefined}
                      className={cn(
                        'data-[placeholder]:text-grey-light',
                        isInvalid && INVALID_CLASS,
                      )}
                    >
                      <SelectValue placeholder={t('form.placeholders.fee_type', 'Select fee type')} />
                    </SelectTrigger>
                    <SelectContent>
                      {FEE_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </FieldContent>
              </Field>
            );
          }}
        </form.Field>

        <form.Field name="fee">
          {(field) => {
            const isInvalid = shouldShowFieldError(field.state.meta, submitted);
            // Affix pattern mirrors EditProductForm / Inventory create+edit /
            // ResaleItem create+edit.
            const isFlat = feeType === FeeTypes.FLAT;
            const isPercentage = feeType === FeeTypes.PERCENTAGE;
            const paddingClass = isFlat ? 'pl-12' : isPercentage ? 'pr-12' : '';

            return (
              <Field>
                <FieldLabel className={LABEL_CLASS}>{t('form.fee', 'Fee')}</FieldLabel>
                <FieldContent>
                  <div className="relative">
                    {isFlat && (
                      <span className="absolute left-5 top-1/2 -translate-y-1/2 font-inter text-sm text-grey">$</span>
                    )}
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      aria-invalid={isInvalid || undefined}
                      value={(field.state.value as number | null) ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        field.handleChange(val === '' ? null : parseFloat(val));
                      }}
                      onBlur={field.handleBlur}
                      placeholder={t('form.placeholders.fee', '10')}
                      className={cn(INPUT_CLASS, paddingClass, isInvalid && INVALID_CLASS)}
                    />
                    {isPercentage && (
                      <span className="absolute right-5 top-1/2 -translate-y-1/2 font-inter text-sm text-grey">%</span>
                    )}
                  </div>
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </FieldContent>
              </Field>
            );
          }}
        </form.Field>
      </div>

      {/* Description (optional) */}
      <form.Field name="description">
        {(field) => (
          <Field>
            <FieldLabelRow
              label={t('form.description', 'Description')}
              marker={t('form.optional', 'Optional')}
              labelClassName={LABEL_CLASS}
            />
            <FieldContent>
              <Textarea
                value={(field.state.value as string | null) ?? ''}
                onChange={(e) => field.handleChange(e.target.value || null)}
                onBlur={field.handleBlur}
                placeholder={t(
                  'form.placeholders.description',
                  '5% discount for end client, 5% referral commission for venue',
                )}
                className={TEXTAREA_CLASS}
              />
            </FieldContent>
          </Field>
        )}
      </form.Field>
    </div>
  );
}
